import { DecimalPipe, NgClass } from "@angular/common";
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  input,
  Output,
  signal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ASSET_TYPES } from "../../../../../enums/assetTypes.enum";
import { Chart, ChartConfiguration } from "chart.js";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { createTimeoutSignal } from "../../../../../shared/utils/timeout.utils";
import { TimeoutComponent } from "../../../../../shared/components/timeout/timeout.component";
import { BAR_COLORS } from "../../../../../constants/colors/greenColors";
import { DoughnutChartData } from "../../../../../models/ui/chartData";
import { SimplebarAngularModule } from "simplebar-angular";

@Component({
  standalone: true,
  selector: "app-doughnut-chart",
  imports: [TranslateModule, NgClass, ProgressSpinnerModule, SimplebarAngularModule, TimeoutComponent],
  templateUrl: "./doughnut-chart.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./doughnut-chart.component.scss",
})
export class DoughnutChartComponent implements AfterViewInit {
  public chartData = input<DoughnutChartData[] | null>();
  public chartId = input<string>();
  public sourceType = input<string>();

  public selectedTimeWindow = signal("this_month");

  @Output() changeTimeframeEvent = new EventEmitter<string>();

  public assetTypes = ASSET_TYPES;
  public energyLossShare!: DoughnutChartData[];
  private chart!: Chart<"doughnut">;

  public loading = computed(() => this.chartData() === null);
  public timeOut = createTimeoutSignal(this.loading);

  public noData = computed(() => {
    const data = this.chartData();
    if (data) {
      return data.length === 0;
    } else {
      return false;
    }
  });

  public noEnergyLoss = computed(() => {
    const data = this.chartData();
    if (data) {
      return data.every((item) => item.absoluteValue === 0);
    } else {
      return false;
    }
  });

  private decimalPipe = inject(DecimalPipe);
  private translate = inject(TranslateService);
  constructor() {
    effect(() => {
      const data = this.chartData();
      if (data) {
        this.chart.data.labels = data.map((item: DoughnutChartData) => item.label);
        this.chart.data.datasets[0].data = data.map((item: DoughnutChartData) => item.absoluteValue);
        this.energyLossShare = data;
        this.chart.update();
      }
    });
  }
  public ngAfterViewInit() {
    const chartData = {
      labels: [],
      datasets: [
        {
          data: [],
          backgroundColor: BAR_COLORS,
        },
      ],
    };

    const doughnutChartConfig: ChartConfiguration<"doughnut"> = {
      type: "doughnut",
      data: chartData,
      options: {
        maintainAspectRatio: false,
        aspectRatio: 0.7,
        cutout: 65,
        plugins: {
          legend: {
            align: "start",
            position: "top",
            display: true,
            labels: {
              color: "#ffffff",
            },
          },
          tooltip: {
            callbacks: {
              label: (context: any) => {
                const value = this.decimalPipe.transform(
                  context.parsed.toFixed(2),
                  undefined,
                  this.translate.currentLang
                );

                return `${value} kWh`;
              },
            },
          },
        },
      },
    };

    const chartId = this.chartId();
    if (!chartId) {
      return;
    }
    this.chart = new Chart(chartId, doughnutChartConfig);
  }

  /**
   * Handles the change event for the time window select dropdown.
   * Updates the selected time window and emits an event with the new value.
   * @param event - The select element triggering the change event.
   */
  public onChangeTimeSelect(event: HTMLSelectElement) {
    const timeWindow = event?.value;
    if (timeWindow) {
      this.selectedTimeWindow.set(timeWindow);
      this.changeTimeframeEvent.emit(timeWindow);
    }
  }
}
